/**
 * 已开启的功能
 * ---------------------------------------------------------------------------
 * 功能开关决定的不只是侧栏那一行灰字。**关掉一个功能，所有依赖它的地方
 * 都要一起消失**——只藏菜单不藏内容的话，门店会在工作台上看到一块
 * 「店内实时状态」，点进去却发现预约排队根本没开。
 *
 * 所以判断逻辑抽在这里一份，侧栏和工作台共用。各写一遍必然有一天
 * 只改了其中一处。
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { featuresApi } from '@/lib/api/endpoints/settings';
import { queryKeys } from '@/lib/api/query-keys';

export function useEnabledFeatures(): {
  features: Set<string>;
  /** 还没拿到开关状态。**加载中不要当成"已关闭"**——那会让页面闪一下少半块 */
  loading: boolean;
} {
  const featuresQuery = useQuery({
    queryKey: queryKeys.features(),
    queryFn: () => featuresApi.list(),
    // 开关很少变，缓存久一点，省掉每次进页面的一次往返
    staleTime: 5 * 60_000,
  });

  /**
   * 真正能用 = **套餐含 且 门店开了**，两个条件缺一不可。
   *
   * 只看 `enabled` 会漏掉一整类情况：**套餐变了，开关没变。**
   * 门店从专业版降到基础版（或者我们调整了某一档含哪些功能）之后，
   * 之前那行 `enabled=true` 还留在表里，于是预约、营销继续白用——
   * 而"开关"这一层本来只是门店自己决定要不要在界面上显示，
   * 不该有授予权限的能力。
   */
  const features = useMemo(
    () =>
      new Set(
        (featuresQuery.data ?? [])
          .filter(
            (feature) => feature.enabled && feature.includedInPlan !== false,
          )
          .map((feature) => feature.featureCode),
      ),
    [featuresQuery.data],
  );

  return { features, loading: featuresQuery.isLoading };
}
